/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';

import { AdaptiveFormShell, Form } from '@/shared/ui/components/form';
import { useAgency } from '@pages/authenticated/agency-pages/AgenciesContext';
import { ProfileTextAreaField, ProfileDrawerSubmitButton } from './profile-drawer-parts';
import { useSaveAgencyProfile } from './use-save-agency-profile';

/** Mirrors the server-side limit in `agency/_update_mixin.py`. */
const DESCRIPTION_MAX_LENGTH = 1000;

const descriptionSchema = z.object({
  description: z.string().max(DESCRIPTION_MAX_LENGTH).optional(),
});

type DescriptionFormData = z.infer<typeof descriptionSchema>;

/**
 * Drawer for writing the agency description shown on the public site.
 *
 * @param props - The drawer props.
 * @param props.open - Whether the drawer is visible.
 * @param props.onClose - Callback invoked to close the drawer.
 * @returns The description drawer element.
 */
export function DescriptionDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation('features/agency-settings');
  const { agency } = useAgency((s) => s);
  const save = useSaveAgencyProfile(onClose);

  const values: DescriptionFormData = { description: agency?.description ?? '' };

  const {
    control, handleSubmit, reset,
    formState: { isSubmitting },
  } = useForm<DescriptionFormData>({
    resolver: zodResolver(descriptionSchema),
    values,
  });

  // Discards edits abandoned on a previous open, and picks up the agency
  // record when it arrives after this drawer has already mounted.
  useEffect(() => {
    if (open) reset(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, agency?.id]);

  return (
    <AdaptiveFormShell
      open={open}
      onClose={onClose}
      density="compact"
      title={t('v2.profile.editDescription')}
      footer={
        <ProfileDrawerSubmitButton
          form="description-form"
          disabled={isSubmitting}
          label={t('v2.profile.save')}
          savingLabel={t('v2.profile.saving')}
        />
      }
    >
      <Form id="description-form" onSubmit={handleSubmit(save)} className="flex flex-col">
        <ProfileTextAreaField
          control={control}
          name="description"
          label={t('v2.profile.description')}
          placeholder={t('v2.profile.descriptionPlaceholder')}
          rows={6}
          maxLength={DESCRIPTION_MAX_LENGTH}
        />
      </Form>
    </AdaptiveFormShell>
  );
}
